/**
 * Template Item List
 * 
 * Sortable list of items within a system with drag-and-drop reordering
 */

import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { GripVertical, Plus, Trash2, Loader2, Camera, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import type { TemplateItem } from '@/hooks/useInspectionTemplateEditor';

interface TemplateItemListProps {
  items: TemplateItem[];
  selectedItemId: string | null;
  onSelectItem: (id: string) => void;
  onAddItem?: (data: { item_key: string; name: string }) => Promise<any>;
  onDeleteItem?: (id: string) => Promise<void>;
  onReorderItems?: (orderedIds: string[]) => Promise<void>;
  disabled?: boolean;
}

interface SortableItemProps {
  item: TemplateItem;
  isSelected: boolean;
  onSelect: () => void;
  onDelete?: () => void;
  disabled?: boolean;
}

function SortableItem({ item, isSelected, onSelect, onDelete, disabled }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const evidenceIcon = item.evidence_mode === 'REQUIRED' ? (
    <Camera className="h-3 w-3 text-primary" />
  ) : item.evidence_mode === 'OPTIONAL' ? (
    <Camera className="h-3 w-3 text-muted-foreground" />
  ) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors',
        isSelected ? 'bg-primary/10 border-primary' : 'bg-card hover:bg-accent',
        isDragging && 'opacity-50',
        disabled && 'opacity-60'
      )}
      onClick={onSelect}
    >
      {!disabled && (
        <button
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium truncate">{item.name}</span>
          {item.is_required && (
            <span className="text-xs text-destructive">*</span>
          )}
        </div>
        {item.description && (
          <p className="text-xs text-muted-foreground truncate">{item.description}</p>
        )}
      </div>
      <div className="flex items-center gap-1">
        {evidenceIcon}
        {!disabled && onDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function TemplateItemList({
  items,
  selectedItemId,
  onSelectItem,
  onAddItem,
  onDeleteItem,
  onReorderItems,
  disabled,
}: TemplateItemListProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && onReorderItems) {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      const newOrder = arrayMove(items, oldIndex, newIndex);
      await onReorderItems(newOrder.map((i) => i.id));
    }
  };

  const handleAddItem = async () => {
    if (!onAddItem || !newItemName.trim()) return;
    
    setIsAdding(true);
    try {
      const key = newItemName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      await onAddItem({ item_key: key, name: newItemName.trim() });
      setNewItemName('');
      setAddDialogOpen(false);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-2">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {items.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              isSelected={item.id === selectedItemId}
              onSelect={() => onSelectItem(item.id)}
              onDelete={onDeleteItem ? () => onDeleteItem(item.id) : undefined}
              disabled={disabled}
            />
          ))}
        </SortableContext>
      </DndContext>

      {items.length === 0 && (
        <div className="flex flex-col items-center py-4 text-muted-foreground">
          <FileText className="h-6 w-6 mb-1" />
          <span className="text-sm">No items yet</span>
        </div>
      )}

      {!disabled && onAddItem && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setAddDialogOpen(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Item
        </Button>
      )}

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Inspection Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="itemName">Item Name</Label>
              <Input
                id="itemName"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="e.g., Brake Pad Thickness, Tire Tread Depth"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddItem} disabled={isAdding || !newItemName.trim()}>
              {isAdding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
